import { ArrowsClockwise, Plus, SealCheck, X } from '@phosphor-icons/react';
import {
  getGetCompetitorStatsQueryKey,
  getListCompetitorPostsQueryKey,
  getListCompetitorsQueryKey,
  getListCompetitorSuggestionsQueryKey,
  useDismissCompetitorSuggestion,
  useListCompetitorSuggestions,
  useRefreshCompetitorSuggestions,
  useTrackCompetitorSuggestion,
} from '@reelplicate/api-client/competitors';
import type { CompetitorSuggestionDto } from '@reelplicate/api-client/model';
import { Button, IconButton, Stack, toast } from '@reelplicate/ui';
import { useQueryClient } from '@tanstack/react-query';

import { platformName } from './format';

const seededBy = (handles: string[]) => {
  const named = handles.map((h) => `@${h}`);
  if (named.length <= 2) return named.join(' and ');
  return `${named.slice(0, 2).join(', ')} and ${named.length - 2} more`;
};

/**
 * "Accounts like yours": accounts Instagram shows alongside the ones you
 * track - those related to more of them first. Track one in a click, or
 * dismiss it for good. Renders nothing until there's something to show.
 */
export function SuggestionsSection({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  const { data } = useListCompetitorSuggestions(workspaceId, {
    query: { enabled: Boolean(workspaceId) },
  });
  const track = useTrackCompetitorSuggestion();
  const dismiss = useDismissCompetitorSuggestion();
  const refresh = useRefreshCompetitorSuggestions();

  const refetch = (all: boolean) =>
    Promise.all(
      [
        getListCompetitorSuggestionsQueryKey(workspaceId),
        ...(all
          ? [
              getListCompetitorsQueryKey(workspaceId),
              getGetCompetitorStatsQueryKey(workspaceId),
              getListCompetitorPostsQueryKey(workspaceId),
            ]
          : []),
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );

  const onTrack = async (suggestion: CompetitorSuggestionDto) => {
    try {
      await track.mutateAsync({ id: workspaceId, suggestionId: suggestion.id });
    } catch {
      return; // Shown as a toast (e.g. the plan's competitors are full).
    }
    await refetch(true);
    toast.success(`Tracking @${suggestion.handle} - their posts show up within the hour`);
  };

  const onDismiss = async (suggestion: CompetitorSuggestionDto) => {
    try {
      await dismiss.mutateAsync({ id: workspaceId, suggestionId: suggestion.id });
    } catch {
      return;
    }
    await refetch(false);
  };

  if (!data || data.items.length === 0) return null;
  return (
    <section aria-labelledby="suggestions-heading" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <Stack gap={0.5} className="flex-1">
          <h2 id="suggestions-heading" className="text-heading-h4 text-text-primary">
            Accounts like yours
          </h2>
          <p className="text-body-sm text-text-secondary">
            Instagram shows these alongside the accounts you track.
          </p>
        </Stack>
        <IconButton
          icon={ArrowsClockwise}
          variant="ghost"
          aria-label="Refresh suggestions"
          disabled={refresh.isPending}
          onClick={() =>
            void refresh
              .mutateAsync({ id: workspaceId })
              .then(() => refetch(false))
              .catch(() => undefined)
          }
        />
      </div>
      <ul className="bg-bg-surface border-border-subtle divide-border-subtle divide-y rounded-xl border">
        {data.items.map((suggestion) => (
          <li key={suggestion.id} className="flex items-center gap-3 px-4 py-3">
            <Stack gap={0.5} className="min-w-0 flex-1">
              <p className="text-body-md-strong text-text-primary inline-flex items-center gap-1">
                <a
                  href={`https://www.instagram.com/${suggestion.handle}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="focus-visible:shadow-focus-ring rounded-xs outline-none hover:underline"
                >
                  @{suggestion.handle}
                </a>
                {suggestion.verified && (
                  <SealCheck
                    aria-label="Verified"
                    weight="fill"
                    className="text-icon-brand size-4"
                  />
                )}
              </p>
              <p className="text-caption text-text-secondary">
                {platformName(suggestion.platform)} · Suggested by{' '}
                {seededBy(suggestion.seedHandles)}
              </p>
            </Stack>
            <Button
              size="sm"
              variant="outline"
              leadingIcon={Plus}
              loading={track.isPending && track.variables?.suggestionId === suggestion.id}
              onClick={() => void onTrack(suggestion)}
            >
              Track
            </Button>
            <IconButton
              icon={X}
              size="sm"
              variant="ghost"
              aria-label={`Dismiss @${suggestion.handle}`}
              onClick={() => void onDismiss(suggestion)}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
